import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import LandingPage from "@/pages/LandingPage";
import StaffLogin from "@/pages/StaffLogin";
import Invite from "@/pages/Invite";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import Schedule from "@/pages/Schedule";
import StaffSchedule from "@/pages/StaffSchedule";
import Subscriptions from "@/pages/Subscriptions";
import Notifications from "@/pages/Notifications";
import StaffManagement from "@/pages/StaffManagement";
import CustomerPortal from "@/pages/CustomerPortal";
import Quotes from "@/pages/Quotes";
import ContentManager from "@/pages/ContentManager";
import Analytics from "@/pages/Analytics";
import TranslationManager from "@/pages/TranslationManager";
import UserManagement from "@/pages/UserManagement";
import FlyerGenerator from "@/pages/admin/FlyerGenerator";
import CutRequests from "@/pages/CutRequests";
import CutReport from "@/pages/CutReport";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { SettingsProvider } from "@/context/SettingsContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { MowerOverlay } from "@/components/MowerOverlay";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";

// Auth callback component to handle session_id
// loginType is encoded in the URL PATH: /auth-callback/{loginType}#session_id=xxx
// This is immune to Safari ITP clearing localStorage/sessionStorage/queryParams
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const { login } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    // Extract loginType from URL path: /auth-callback/{loginType}
    const pathParts = window.location.pathname.split("/");
    const callbackIndex = pathParts.indexOf("auth-callback");
    const loginType = callbackIndex >= 0 && pathParts[callbackIndex + 1] 
      ? pathParts[callbackIndex + 1] 
      : "customer";
    
    console.log("AuthCallback - path:", window.location.pathname, "loginType:", loginType);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      // Clean up any legacy storage
      localStorage.removeItem("loginType");
      sessionStorage.removeItem("loginType");
      login(sessionId, loginType);
    } else {
      navigate("/");
    }
  }, [navigate, login]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-[#EDEDED]">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected route wrapper
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If user is passed via state (from login redirect), skip checkAuth
    if (location.state?.user || user) return;
    checkAuth();
  }, [checkAuth, location.state, user]);

  useEffect(() => {
    if (!loading && !user && !location.state?.user) {
      navigate("/");
    }
    const currentUser = user || location.state?.user;
    if (!loading && currentUser && allowedRoles.length > 0 && !allowedRoles.includes(currentUser.role)) {
      // Redirect based on role
      if (currentUser.role === "admin") {
        navigate("/dashboard");
      } else if (currentUser.role === "staff") {
        navigate("/staff-schedule");
      } else {
        navigate("/portal");
      }
    }
  }, [loading, user, navigate, allowedRoles, location.state]);

  // Use user from state if available (during login redirect)
  const currentUser = user || location.state?.user;

  if (loading && !currentUser) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#39FF14] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!currentUser) return null;
  if (allowedRoles.length > 0 && !allowedRoles.includes(currentUser.role)) return null;

  return children;
};

// Router component that handles session_id detection
const AppRouter = () => {
  const location = useLocation();

  // Check URL fragment for session_id BEFORE rendering routes
  // Handles /auth-callback/{loginType}#session_id=xxx
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* Customer-facing landing page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Auth callback route (renders when no hash, e.g., direct visit) */}
      <Route path="/auth-callback/*" element={<AuthCallback />} />
      
      {/* Staff/Admin login page */}
      <Route path="/staff-login" element={<StaffLogin />} />

      {/* Public customer invite link */}
      <Route path="/invite/:token" element={<Invite />} />
      
      {/* Admin routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/customers"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Customers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedule"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Schedule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscriptions"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Subscriptions />
          </ProtectedRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Notifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-management"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <StaffManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/quotes"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Quotes />
          </ProtectedRoute>
        }
      />
      <Route
        path="/content-manager"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <ContentManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <Analytics />
          </ProtectedRoute>
        }
      />
      <Route
        path="/translations"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <TranslationManager />
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <UserManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/flyer-generator"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <FlyerGenerator />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cut-requests"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CutRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cut-report"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <CutReport />
          </ProtectedRoute>
        }
      />
      
      {/* Staff routes */}
      <Route
        path="/staff-schedule"
        element={
          <ProtectedRoute allowedRoles={["staff", "admin"]}>
            <StaffSchedule />
          </ProtectedRoute>
        }
      />
      
      {/* Customer portal */}
      <Route
        path="/portal"
        element={
          <ProtectedRoute allowedRoles={["customer"]}>
            <CustomerPortal />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
           <SettingsProvider>
            <AppRouter />
            <LanguageSwitcher />
            <MowerOverlay />
            <PWAInstallPrompt />
            <Toaster 
              position="top-right"
              toastOptions={{
                style: {
                  background: '#121212',
                  border: '1px solid #39FF14',
                  color: '#EDEDED',
                },
              }}
            />
           </SettingsProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
